import type { Editor } from "./canvas";
import type { TLCommentThread, TLShape, TLShapeId } from "@tldraw/tlschema";
import type { Point } from "../shared/schema";

export type CommentRegion = {
  x: number;
  y: number;
  w: number;
  h: number;
  pinX: 0 | 1;
  pinY: 0 | 1;
};

export type PendingRegionComment = {
  kind: "region";
  /** The shape the region follows; null keeps it in page space. */
  target: TLShape | null;
  region: CommentRegion;
  stroke: Point[];
};

type BoundsLike = { x: number; y: number; w: number; h: number };
type StoredReviewRegion = CommentRegion & { shapeId: string };
type StoredReviewStroke = { points: Point[] };

export function clampCommentPoint(point: Point, bounds: BoundsLike): Point {
  return {
    x: Math.max(bounds.x, Math.min(bounds.x + bounds.w, point.x)),
    y: Math.max(bounds.y, Math.min(bounds.y + bounds.h, point.y)),
  };
}

export function commentRegionFromDrag(a: Point, b: Point): CommentRegion {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
    pinX: b.x < a.x ? 0 : 1,
    pinY: b.y < a.y ? 0 : 1,
  };
}

export function commentRegionPoints(region: CommentRegion): Point[] {
  return [
    { x: region.x, y: region.y },
    { x: region.x + region.w, y: region.y },
    { x: region.x + region.w, y: region.y + region.h },
    { x: region.x, y: region.y + region.h },
  ];
}

export function boundsForPoints(points: Point[]): BoundsLike {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    w: Math.max(...xs) - x,
    h: Math.max(...ys) - y,
  };
}

export function commentStrokeLength(points: Point[]) {
  return points.slice(1).reduce((length, point, index) => {
    const previous = points[index];
    return length + Math.hypot(point.x - previous.x, point.y - previous.y);
  }, 0);
}

export function commentRegionForStroke(
  points: Point[],
  padding: number,
  bounds: BoundsLike,
): CommentRegion {
  const strokeBounds = boundsForPoints(points);
  const x = Math.max(bounds.x, strokeBounds.x - padding);
  const y = Math.max(bounds.y, strokeBounds.y - padding);
  const maxX = Math.min(
    bounds.x + bounds.w,
    strokeBounds.x + strokeBounds.w + padding,
  );
  const maxY = Math.min(
    bounds.y + bounds.h,
    strokeBounds.y + strokeBounds.h + padding,
  );
  const end = points[points.length - 1];
  return {
    x,
    y,
    w: Math.max(0, maxX - x),
    h: Math.max(0, maxY - y),
    pinX: end.x < x + (maxX - x) / 2 ? 0 : 1,
    pinY: end.y < y + (maxY - y) / 2 ? 0 : 1,
  };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function storedReviewRegion(
  thread: TLCommentThread,
): StoredReviewRegion | null {
  const value = thread.meta.reviewRegion;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.shapeId !== "string" ||
    !isFiniteNumber(candidate.x) ||
    !isFiniteNumber(candidate.y) ||
    !isFiniteNumber(candidate.w) ||
    !isFiniteNumber(candidate.h) ||
    (candidate.pinX !== 0 && candidate.pinX !== 1) ||
    (candidate.pinY !== 0 && candidate.pinY !== 1) ||
    candidate.w < 0 ||
    candidate.h < 0
  )
    return null;
  return candidate as StoredReviewRegion;
}

function storedReviewStroke(
  thread: TLCommentThread,
): StoredReviewStroke | null {
  const value = thread.meta.reviewStroke;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const points = (value as Record<string, unknown>).points;
  if (!Array.isArray(points) || points.length < 2 || points.length > 240)
    return null;
  if (
    !points.every(
      (point) =>
        point &&
        typeof point === "object" &&
        !Array.isArray(point) &&
        isFiniteNumber((point as Record<string, unknown>).x) &&
        isFiniteNumber((point as Record<string, unknown>).y),
    )
  )
    return null;
  return { points: points as Point[] };
}

/** A region in page space, for a selection that no single shape holds. */
export function commentPageRegionRecord(region: CommentRegion) {
  return {
    anchor: {
      type: "region" as const,
      ...boundsForPoints(commentRegionPoints(region)),
      pinX: region.pinX,
      pinY: region.pinY,
    },
    meta: {},
  };
}

export function commentRegionRecord(
  editor: Editor,
  target: TLShape,
  region: CommentRegion,
  stroke?: Point[],
) {
  const geometry = editor.getShapeGeometry(target).bounds;
  const geometryWidth = geometry.w || 1;
  const geometryHeight = geometry.h || 1;
  const transform = editor.getShapePageTransform(target)!;
  const pagePoints = commentRegionPoints(region).map((point) =>
    transform.applyToPoint(point),
  );
  const pageBounds = boundsForPoints(pagePoints);
  const pinPoint = transform.applyToPoint({
    x: region.x + region.w * region.pinX,
    y: region.y + region.h * region.pinY,
  });
  return {
    anchor: {
      type: "region" as const,
      ...pageBounds,
      pinX: pageBounds.w ? (pinPoint.x - pageBounds.x) / pageBounds.w : 1,
      pinY: pageBounds.h ? (pinPoint.y - pageBounds.y) / pageBounds.h : 1,
    },
    meta: {
      reviewRegion: {
        shapeId: target.id,
        x: (region.x - geometry.x) / geometryWidth,
        y: (region.y - geometry.y) / geometryHeight,
        w: region.w / geometryWidth,
        h: region.h / geometryHeight,
        pinX: region.pinX,
        pinY: region.pinY,
      },
      ...(stroke && stroke.length > 1
        ? {
            reviewStroke: {
              points: stroke.slice(0, 240).map((point) => ({
                x: (point.x - geometry.x) / geometryWidth,
                y: (point.y - geometry.y) / geometryHeight,
              })),
            },
          }
        : {}),
    },
  };
}

export function commentStrokePagePoints(
  editor: Editor,
  thread: TLCommentThread,
): Point[] | null {
  const region = storedReviewRegion(thread);
  const stroke = storedReviewStroke(thread);
  if (!region || !stroke) return null;
  const shape = editor.getShape(region.shapeId as TLShapeId);
  if (!shape) return null;
  const geometry = editor.getShapeGeometry(shape).bounds;
  const transform = editor.getShapePageTransform(shape);
  if (!transform) return null;
  return stroke.points.map((point) =>
    transform.applyToPoint({
      x: geometry.x + point.x * geometry.w,
      y: geometry.y + point.y * geometry.h,
    }),
  );
}

export function commentRegionPagePoints(
  editor: Editor,
  thread: TLCommentThread,
): Point[] | null {
  if (thread.anchor.type !== "region") return null;
  const stored = storedReviewRegion(thread);
  if (stored) {
    const shape = editor.getShape(stored.shapeId as TLShapeId);
    if (shape) {
      const geometry = editor.getShapeGeometry(shape).bounds;
      const transform = editor.getShapePageTransform(shape);
      if (transform) {
        const region: CommentRegion = {
          x: geometry.x + stored.x * geometry.w,
          y: geometry.y + stored.y * geometry.h,
          w: stored.w * geometry.w,
          h: stored.h * geometry.h,
          pinX: stored.pinX,
          pinY: stored.pinY,
        };
        return commentRegionPoints(region).map((point) =>
          transform.applyToPoint(point),
        );
      }
    }
  }
  return commentRegionPoints({
    x: thread.anchor.x,
    y: thread.anchor.y,
    w: thread.anchor.w,
    h: thread.anchor.h,
    pinX: thread.anchor.pinX && thread.anchor.pinX < 0.5 ? 0 : 1,
    pinY: thread.anchor.pinY && thread.anchor.pinY < 0.5 ? 0 : 1,
  });
}

export function commentRegionPin(
  thread: TLCommentThread,
  points: Point[],
): Point {
  const stored = storedReviewRegion(thread);
  const pinX =
    stored?.pinX ??
    (thread.anchor.type === "region" && thread.anchor.pinX! < 0.5 ? 0 : 1);
  const pinY =
    stored?.pinY ??
    (thread.anchor.type === "region" && thread.anchor.pinY! < 0.5 ? 0 : 1);
  return points[pinY ? (pinX ? 2 : 3) : pinX ? 1 : 0];
}

export function commentRegionPageBounds(
  editor: Editor,
  thread: TLCommentThread,
) {
  const points = commentRegionPagePoints(editor, thread);
  return points ? boundsForPoints(points) : null;
}
